import { QueryParams } from '@Activity/common/constants';
import SearchBarFilter from '@common/organisms/SearchBarFilter';
import ListFilters from '@common/ui/ListFilters/ListFilters';
import { Stack } from '@mui/material';
import { useCallback, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import { DateFilter } from '@common/organisms/CommonFilters/DateFilter';
import SystemLogActionFilter, { ACTION_FILTER_LABEL } from './filters/SystemLogActionFilter';
import SystemLogActorsFilter, { ACTOR_FILTER_LABEL } from './filters/SystemLogActorsFilter';
import SystemLogSourceFilter, { SOURCE_FILTER_LABEL } from './filters/SystemLogSourceFilter';
import SystemLogTargetTypeFilter, { TARGET_TYPE_FILTER_LABEL } from './filters/SystemLogTargetTypeFilter';
import SystemLogTargetFilter, { TARGET_FILTER_LABEL } from './filters/SystemLogTargetFilter';

export const ACTION_FILTER_PARAM = 'action';
export const SOURCE_FILTER_PARAM = 'source';
export const ACTOR_FILTER_PARAM = 'actor';
export const TARGET_TYPE_FILTER_PARAM = 'target_type';
export const TARGET_FILTER_PARAM = 'target';

const DEFAULT_VISIBLE_FILTERS = [
  ACTION_FILTER_PARAM,
  SOURCE_FILTER_PARAM,
  ACTOR_FILTER_PARAM,
  TARGET_TYPE_FILTER_PARAM,
  TARGET_FILTER_PARAM,
];

export default function SystemLogFilters() {
  const [openedFilter, setOpenedFilter] = useState<string | undefined>(undefined);
  const [searchParams, setSearchParams] = useSearchParams();

  const selected = useMemo(
    () => ({
      [ACTION_FILTER_PARAM]: searchParams.getAll(ACTION_FILTER_PARAM),
      [SOURCE_FILTER_PARAM]: searchParams.getAll(SOURCE_FILTER_PARAM),
      [ACTOR_FILTER_PARAM]: searchParams.getAll(ACTOR_FILTER_PARAM),
      [TARGET_TYPE_FILTER_PARAM]: searchParams.getAll(TARGET_TYPE_FILTER_PARAM),
      [TARGET_FILTER_PARAM]: searchParams.getAll(TARGET_FILTER_PARAM),
    }),
    [searchParams],
  );

  const onCloseFilter = () => setOpenedFilter(undefined);
  const onOpenFilter = (value: string) => setOpenedFilter(value);

  const applySearchParams = useCallback(
    (newSearchParams: URLSearchParams) => {
      newSearchParams.delete(QueryParams.page);
      setSearchParams(newSearchParams);
    },
    [setSearchParams],
  );

  const onSelect = useCallback(
    ({ values, filterParam }: { values: string[]; filterParam: string }) => {
      const newSearchParams = new URLSearchParams(searchParams);
      newSearchParams.delete(filterParam);
      for (const value of values) {
        newSearchParams.append(filterParam, value);
      }
      applySearchParams(newSearchParams);
    },
    [applySearchParams, searchParams],
  );

  const onClear = useCallback(
    (filterParam: string) => {
      const newSearchParams = new URLSearchParams(searchParams);
      newSearchParams.delete(filterParam);
      applySearchParams(newSearchParams);
    },
    [applySearchParams, searchParams],
  );

  const filters = useMemo(
    () => [
      {
        name: ACTION_FILTER_LABEL,
        queryParam: ACTION_FILTER_PARAM,
        element: (
          <SystemLogActionFilter
            opened={openedFilter === ACTION_FILTER_PARAM}
            onClose={onCloseFilter}
            onOpen={() => onOpenFilter(ACTION_FILTER_PARAM)}
            selected={selected[ACTION_FILTER_PARAM]}
            onClear={() => onClear(ACTION_FILTER_PARAM)}
            onSelect={(values: string[]) => onSelect({ values, filterParam: ACTION_FILTER_PARAM })}
          />
        ),
      },
      {
        name: SOURCE_FILTER_LABEL,
        queryParam: SOURCE_FILTER_PARAM,
        element: (
          <SystemLogSourceFilter
            opened={openedFilter === SOURCE_FILTER_PARAM}
            onClose={onCloseFilter}
            onOpen={() => onOpenFilter(SOURCE_FILTER_PARAM)}
            selected={selected[SOURCE_FILTER_PARAM]}
            onClear={() => onClear(SOURCE_FILTER_PARAM)}
            onSelect={(values: string[]) => onSelect({ values, filterParam: SOURCE_FILTER_PARAM })}
          />
        ),
      },
      {
        name: ACTOR_FILTER_LABEL,
        queryParam: ACTOR_FILTER_PARAM,
        element: (
          <SystemLogActorsFilter
            opened={openedFilter === ACTOR_FILTER_PARAM}
            onClose={onCloseFilter}
            onOpen={() => onOpenFilter(ACTOR_FILTER_PARAM)}
            selected={selected[ACTOR_FILTER_PARAM]}
            onClear={() => onClear(ACTOR_FILTER_PARAM)}
            onSelect={(values: string[]) => onSelect({ values, filterParam: ACTOR_FILTER_PARAM })}
          />
        ),
      },
      {
        name: TARGET_TYPE_FILTER_LABEL,
        queryParam: TARGET_TYPE_FILTER_PARAM,
        element: (
          <SystemLogTargetTypeFilter
            opened={openedFilter === TARGET_TYPE_FILTER_PARAM}
            onClose={onCloseFilter}
            onOpen={() => onOpenFilter(TARGET_TYPE_FILTER_PARAM)}
            selected={selected[TARGET_TYPE_FILTER_PARAM]}
            onClear={() => onClear(TARGET_TYPE_FILTER_PARAM)}
            onSelect={(values: string[]) => onSelect({ values, filterParam: TARGET_TYPE_FILTER_PARAM })}
          />
        ),
      },
      {
        name: TARGET_FILTER_LABEL,
        queryParam: TARGET_FILTER_PARAM,
        element: (
          <SystemLogTargetFilter
            opened={openedFilter === TARGET_FILTER_PARAM}
            onClose={onCloseFilter}
            onOpen={() => onOpenFilter(TARGET_FILTER_PARAM)}
            selected={selected[TARGET_FILTER_PARAM]}
            onClear={() => onClear(TARGET_FILTER_PARAM)}
            onSelect={(values: string[]) => onSelect({ values, filterParam: TARGET_FILTER_PARAM })}
          />
        ),
      },
    ],
    [onClear, onSelect, openedFilter, selected],
  );

  const [visibleFilters] = useState(DEFAULT_VISIBLE_FILTERS);

  const hasActiveFilters = useMemo(() => {
    for (const filter of filters) {
      if (searchParams.has(filter.queryParam)) {
        return true;
      }
    }
    return false;
  }, [filters, searchParams]);

  const handleOnClear = useCallback(() => {
    const newSearchParams = new URLSearchParams(searchParams);
    for (const filter of filters) {
      newSearchParams.delete(filter.queryParam);
    }
    for (const dateParam of [QueryParams.startDate, QueryParams.endDate, QueryParams.utc, QueryParams.period]) {
      newSearchParams.delete(dateParam);
    }
    applySearchParams(newSearchParams);
  }, [applySearchParams, filters, searchParams]);

  const handleOnAddFilter = (qp: string) => {
    setOpenedFilter(qp);
  };

  return (
    <ListFilters
      visibleFilters={visibleFilters}
      filters={filters}
      onAddFilter={handleOnAddFilter}
      onClear={hasActiveFilters ? handleOnClear : undefined}
      prefix={
        <Stack direction="row" spacing={2}>
          <SearchBarFilter onSetSearchParams={applySearchParams} placeholder="Search by actor or target" />
          <DateFilter />
        </Stack>
      }
    />
  );
}
